import type { EvidenceStatus, RiskEvidence } from "../schemas";

export type RiskTier = "UNAVAILABLE" | "LOW" | "MODERATE" | "HIGH" | "CRITICAL";
export type RiskConfidence = "LOW" | "MEDIUM" | "HIGH";

export interface RiskAssessment {
  /** Verified/degraded adverse points already observed. */
  minScore: number;
  /** Worst case while unavailable signals remain unresolved. */
  maxScore: number;
  /** Share of the 100-point model backed by fully verified signals. */
  coveragePct: number;
  tier: RiskTier;
  confidence: RiskConfidence;
  reasons: string[];
  gaps: string[];
}

interface Signal {
  label: string;
  status: EvidenceStatus;
  weight: number;
  points: number;
  reason: string | undefined;
}

function concentrationPoints(value: number): number {
  return value >= 35 ? 25 : value >= 20 ? 15 : value >= 10 ? 7 : 0;
}

function coordinationPoints(wallets: number, supplyPct: number): number {
  return wallets >= 8 && supplyPct >= 15 ? 15 : wallets >= 4 && supplyPct >= 10 ? 8 : 0;
}

function linkedFundingPoints(value: number): number {
  return value >= 6 ? 15 : value >= 3 ? 8 : 0;
}

function recurrencePoints(value: number): number {
  return value >= 10 ? 10 : value >= 4 ? 6 : value >= 2 ? 3 : 0;
}

function rugPoints(value: number): number {
  return value >= 3 ? 20 : value >= 1 ? 12 : 0;
}

function holdingsPoints(value: number): number {
  return value >= 20 ? 15 : value >= 10 ? 10 : value >= 5 ? 5 : 0;
}

/**
 * Coverage-aware interval scoring.
 *
 * Verified signals collapse to one exact contribution. Degraded signals can
 * establish an adverse lower bound but retain their full unknown upside.
 * Unavailable signals span zero to their full weight. This means missing data
 * can never manufacture a LOW score.
 */
export function assessRisk(evidence: RiskEvidence): RiskAssessment {
  const concentration = concentrationPoints(evidence.blockSupplyPct);
  const coordination = coordinationPoints(evidence.blockWallets, evidence.blockSupplyPct);
  const linked = linkedFundingPoints(evidence.linkedWallets);
  const recurrence = recurrencePoints(evidence.creatorPriorListings);
  const rugs = rugPoints(evidence.creatorPriorRugs);
  const holdings = holdingsPoints(evidence.creatorHoldingsPct);
  const signals: Signal[] = [
    {
      label: "listing-block concentration",
      status: evidence.blockActivityStatus,
      weight: 25,
      points: concentration,
      reason: concentration
        ? `${evidence.blockSupplyPct}% of supply accumulated in the listing block`
        : undefined,
    },
    {
      label: "listing-block distribution",
      status: evidence.blockActivityStatus,
      weight: 15,
      points: coordination,
      reason: coordination
        ? `${evidence.blockWallets} positive-balance addresses were observed in the listing block`
        : undefined,
    },
    {
      label: "linked-wallet funding",
      status: evidence.fundingLineageStatus,
      weight: 15,
      points: linked,
      reason: linked ? `${evidence.linkedWallets} wallets share funding lineage` : undefined,
    },
    {
      label: "creator recurrence",
      status: evidence.creatorHistoryStatus,
      weight: 10,
      points: recurrence,
      reason: recurrence
        ? `${evidence.creatorPriorListings} prior indexed listings share this creator`
        : undefined,
    },
    {
      label: "creator rug history",
      status: evidence.rugHistoryStatus,
      weight: 20,
      points: rugs,
      reason: rugs ? `${evidence.creatorPriorRugs} prior creator rugs` : undefined,
    },
    {
      label: "creator holdings",
      status: evidence.creatorHoldingsStatus,
      weight: 15,
      points: holdings,
      reason: holdings ? `${evidence.creatorHoldingsPct}% creator holdings` : undefined,
    },
  ];

  let minScore = 0;
  let maxScore = 0;
  let verifiedWeight = 0;
  const reasons: string[] = [];
  const gaps: string[] = [];
  for (const signal of signals) {
    if (signal.status === "verified") {
      minScore += signal.points;
      maxScore += signal.points;
      verifiedWeight += signal.weight;
    } else if (signal.status === "degraded") {
      minScore += signal.points;
      maxScore += signal.weight;
      gaps.push(`${signal.label} is bounded/partial`);
    } else {
      maxScore += signal.weight;
      gaps.push(`${signal.label} is unavailable`);
    }
    if (signal.points > 0 && signal.status !== "unavailable" && signal.reason) {
      reasons.push(signal.reason);
    }
  }

  const tier: RiskTier =
    minScore >= 70
      ? "CRITICAL"
      : minScore >= 40
        ? "HIGH"
        : minScore >= 20
          ? "MODERATE"
          : maxScore < 20
            ? "LOW"
            : "UNAVAILABLE";
  const confidence: RiskConfidence =
    verifiedWeight >= 80 ? "HIGH" : verifiedWeight >= 50 ? "MEDIUM" : "LOW";

  return {
    minScore,
    maxScore,
    coveragePct: verifiedWeight,
    tier,
    confidence,
    reasons,
    gaps,
  };
}
